import { useXNGSelector } from "../../../context/store";
import { selectLoggedInClientAssignment, selectUser } from "../../../context/slices/userProfileSlice";
import { ROUTES_XLOGS } from "../../../constants/URLs";
import { ButtonSidebarItemProps } from "../../../design/types/sidebar_content";
import { MSBICONS } from "../../../fortitude";
import { Accordion, AccordionSummary, AccordionDetails, Typography } from "@mui/material";
import ButtonSidebarItem from "../../../design/high-level/sidebar/sidebar_item_button";
import { XNGIconRenderer, XNGICONS } from "../../../design";
import SchoolIcon from "@mui/icons-material/School";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { READ_ONLY_CALENDAR_PARAM } from "../../calendar/hooks/use_is_read_only_calendar";
import useGoodFaithEffortFlag from "../views/good_faith_effort/hooks/use_gfe_feature_flag";

function useSidebarLayoutTabs() {
  const studentMergeActive = useXNGSelector(
    (state) => state.featureFlags.flags["StudentMergeActive"],
  );
  const adminOrderedServicesActive = useXNGSelector(
    (state) => state.featureFlags.flags["AdminOrderedServicesActive"],
  );
  const calendarManagementActive = useXNGSelector(
    (state) => state.featureFlags.flags["CalendarManagementActive"],
  );
  const adminReadOnlyCalendarActive = useXNGSelector(
    (state) => state.featureFlags.flags["AdminReadOnlyCalendarActive"],
  );
  const transportationAccommodationsAdminActive = useXNGSelector(
    (state) => state.featureFlags.flags["TransportationAccommodationsAdminActive"],
  );
  const goodFaithEffortActive = useGoodFaithEffortFlag();
  const unassignedStudentsActive = useXNGSelector(
    (state) => state.featureFlags.flags["UnassignedStudentsActive"],
  );
  const user = useXNGSelector(selectUser);
  const loggedInClientAssignment = useXNGSelector(selectLoggedInClientAssignment);
  const billingPrototypeActive = useXNGSelector(
    (state) => state.featureFlags.flags["BillingPrototypeActive"],
  );

  const userManagementButtons: ButtonSidebarItemProps[] = [
    {
      icon: <MSBICONS.AvatarWithCircleOutline />,
      label: "User Approvals",
      route: ROUTES_XLOGS._admin.userApproval,
    },
    {
      icon: <MSBICONS.TwoPeople />,
      label: "Staff Directory Manager",
      route: ROUTES_XLOGS._admin.staffDirectoryManager,
    },
    {
      icon: <MSBICONS.PrescribingProviderIcon />,
      label: "Prescribing Providers",
      route: ROUTES_XLOGS._admin.prescribingProvider,
    },
    {
      icon: <MSBICONS.Home />,
      label: "Campus Management",
      route: ROUTES_XLOGS._admin.campusManagement,
    },
  ];

  const caseloadManagementButtons: ButtonSidebarItemProps[] = [
    {
      icon: <MSBICONS.Hierarchy />,
      label: "Approver Caseloads",
      route: ROUTES_XLOGS._admin.approverCaseloads,
    },
    {
      icon: <MSBICONS.Hierarchy />,
      label: "Data Entry Clerk Caseloads",
      route: ROUTES_XLOGS._admin.decCaseloads,
    },
  ];

  const studentManagementButtons: ButtonSidebarItemProps[] = [
    {
      icon: <SchoolIcon fontSize="small" />,
      label: "Student Details",
      route: ROUTES_XLOGS._admin.studentManagement,
    },
  ];

  const calendarManagementButtons: ButtonSidebarItemProps[] = [];

  const toolsButtons: ButtonSidebarItemProps[] = [];

  const unassignedButtons: ButtonSidebarItemProps[] = [];

  if (unassignedStudentsActive) {
    studentManagementButtons.push({
      icon: <MSBICONS.TwoPeople />,
      label: "Unassigned Students",
      route: ROUTES_XLOGS._admin.unassignedStudents,
    });
  }

  if (goodFaithEffortActive) {
    studentManagementButtons.push({
      icon: <MedicalInformationIcon fontSize="small" />,
      label: "Good Faith Effort Tracking",
      route: ROUTES_XLOGS._admin.goodFaithEffort,
    });
  }

  if (calendarManagementActive) {
    calendarManagementButtons.push({
      icon: <MSBICONS.Calendar />,
      label: "School Calendar",
      route: ROUTES_XLOGS._admin.schoolCalendar,
    });
  }

  if (adminReadOnlyCalendarActive) {
    calendarManagementButtons.push({
      icon: <MSBICONS.Calendar />,
      label: "Admin Calendar",
      route: `${ROUTES_XLOGS.calendar}?${READ_ONLY_CALENDAR_PARAM}=true`,
    });
  }

  if (studentMergeActive) {
    toolsButtons.push({
      icon: <MSBICONS.Merge />,
      label: "Student Merge",
      route: ROUTES_XLOGS._admin.studentMerge,
    });
  }

  if (adminOrderedServicesActive) {
    toolsButtons.push({
      icon: <PriceCheckIcon fontSize="small" />,
      label: "Ordered Services",
      route: ROUTES_XLOGS._admin.billableServices,
    });
  }

  if (transportationAccommodationsAdminActive) {
    toolsButtons.push({
      icon: <MSBICONS.Home />,
      label: "Transportation Accommodations/Modifications",
      route: ROUTES_XLOGS._admin.transportationAccommodations,
    });
  }

  // The district billing worklist (XLOGS-195) is the Executive Admin's own page: the admin
  // guard admits Delegated Admins too, so the entry checks the role itself.
  const billingButtons: ButtonSidebarItemProps[] = [];
  if (billingPrototypeActive && loggedInClientAssignment.isExecutiveAdmin) {
    billingButtons.push({
      icon: <FactCheckOutlinedIcon fontSize="small" />,
      label: "Billing Worklist",
      route: ROUTES_XLOGS._admin.billingWorklist,
    });
  }

  // MSB billing and import surfaces live in the MSB zone (XLOGS-205/211) — see views/msb.
  return { userManagementButtons, studentManagementButtons, caseloadManagementButtons, calendarManagementButtons, toolsButtons, unassignedButtons, billingButtons };
}

export default useSidebarLayoutTabs;
